import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckBadgeIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  Modal,
  PageHeader,
  PageState,
  StatusBadge,
} from './OutreachShared';
import {
  focusRing,
  formatDateTime,
  humanize,
  listFrom,
  unwrapResponse,
  useOutreachResource,
} from './outreachUtils';

const EMPTY_TEMPLATE = {};
const PREVIEW_VARIANTS = [
  { key: 'with_name', label: 'With first name' },
  { key: 'without_name', label: 'Without first name' },
];
const EMPTY_EVIDENCE = {
  desktop: false,
  mobile: false,
  text: false,
  footer: false,
  cta: false,
};

function getTemplate(rows, templateKey, version) {
  return rows.find((row) => {
    const key = row.templateKey || row.template_key || row.key;
    const rowVersion = row.version || row.templateVersion || row.template_version;
    return key === templateKey && String(rowVersion) === String(version);
  });
}

export default function OutreachTemplateDetailPage() {
  const params = useParams();
  const templateKey = decodeURIComponent(params.templateKey || '');
  const version = decodeURIComponent(params.version || '');
  const [variant, setVariant] = useState('with_name');
  const [dialog, setDialog] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [approvalEvidence, setApprovalEvidence] = useState(EMPTY_EVIDENCE);

  const resource = useOutreachResource(
    async () => {
      const [templatesResponse, previewResponse, summaryResponse] = await Promise.all([
        founderDashboardAPI.getOutreachTemplates(),
        founderDashboardAPI.getOutreachTemplatePreview(templateKey, version, variant),
        founderDashboardAPI.getOutreachSummary(),
      ]);
      const templatesPayload = unwrapResponse(templatesResponse);
      const previewPayload = unwrapResponse(previewResponse);
      const preview = previewPayload.preview || previewPayload;
      const template = getTemplate(
        listFrom(templatesPayload, 'templates', 'items', 'rows'),
        templateKey,
        version
      );
      return {
        data: {
          ...(template || {}),
          ...preview,
          testAllowlistCount:
            unwrapResponse(summaryResponse)?.prerequisites?.testAllowlistCount || 0,
        },
      };
    },
    [templateKey, version, variant]
  );

  const template = resource.data || EMPTY_TEMPLATE;
  const variants = useMemo(() => PREVIEW_VARIANTS, []);

  useEffect(() => {
    if (!variants.some((item) => item.key === variant)) setVariant(variants[0].key);
  }, [variant, variants]);

  const approvalHistory = template.approvalHistory ||
    template.approval_history ||
    template.approvals ||
    (template.approvedAt
      ? [{
          status: template.approved ? 'approved' : 'invalidated',
          createdAt: template.approvedAt,
          evidence: template.sourceChanged
            ? 'A later source change invalidated this approval.'
            : 'Exact checksum and source revision approved.',
        }]
      : []);
  const checksum =
    template.checksum || template.templateChecksum || template.template_checksum || '—';
  const approvalStatus =
    template.approvalStatus ||
    template.approval_status ||
    (template.approved ? 'approved' : 'pending');
  const html = template.html || template.htmlPreview || template.html_preview || '';
  const plainText =
    template.plainText || template.plain_text || template.text || 'No plain-text preview returned.';
  const confirmationVersion =
    template.confirmationVersion ||
    template.confirmation_version ||
    template.revision ||
    checksum;
  const sourceRevision = template.sourceRevision || template.source_revision || template.revision;
  const approvalEvidenceComplete = Object.values(approvalEvidence).every(Boolean);
  const addressLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedAddress);

  async function approve() {
    setBusy(true);
    setMutationError(null);
    try {
      await founderDashboardAPI.approveOutreachTemplate(templateKey, version, {
        checksum,
        sourceRevision,
        evidence: approvalEvidence,
      });
      setSuccess('The exact template version and checksum were approved.');
      setDialog(null);
      await resource.reload();
    } catch (error) {
      setMutationError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'The template was not approved.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function testSend() {
    if (!addressLooksValid) return;
    setBusy(true);
    setMutationError(null);
    try {
      await founderDashboardAPI.testOutreachTemplate(templateKey, version, {
        fixture: variant,
        address: selectedAddress,
      });
      setSuccess(`A test delivery was queued for the selected internal address.`);
      setDialog(null);
      await resource.reload();
    } catch (error) {
      setMutationError(
        error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'The internal test delivery was not queued.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Link
        to="/outreach/templates"
        className={`inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 ${focusRing}`}
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to templates
      </Link>

      <PageHeader
        title={template.name || humanize(templateKey)}
        description={`Template ${templateKey} · version ${version}`}
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />

      {success ? (
        <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {success}
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {mutationError}
        </div>
      ) : null}

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        onRetry={resource.reload}
      >
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <label className="block">
              <span className="text-sm font-semibold text-gray-800">Synthetic fixture variant</span>
              <select
                value={variant}
                onChange={(event) => setVariant(event.target.value)}
                className={`mt-2 block min-h-[44px] w-full min-w-64 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 ${focusRing}`}
              >
                {variants.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                Preview data is synthetic or masked; no customer acquisition details are shown.
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setApprovalEvidence(EMPTY_EVIDENCE);
                  setDialog('approve');
                }}
                disabled={approvalStatus === 'approved' || checksum === '—'}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
              >
                <CheckBadgeIcon className="h-5 w-5" />
                {approvalStatus === 'approved' ? 'Approved' : 'Approve checksum'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedAddress('');
                  setDialog('test');
                }}
                disabled={Number(template.testAllowlistCount || 0) === 0}
                className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
              >
                <EnvelopeIcon className="h-5 w-5" />
                Internal test send
              </button>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-3">
              <h4 className="font-semibold text-gray-900">Sandboxed HTML preview</h4>
            </div>
            {html ? (
              <iframe
                title={`${humanize(templateKey)} HTML preview`}
                sandbox=""
                srcDoc={html}
                className="h-[680px] w-full bg-white"
              />
            ) : (
              <div className="flex h-64 items-center justify-center p-6 text-sm text-gray-500">
                No HTML preview returned for this fixture.
              </div>
            )}
          </section>

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-5 py-3">
              <h4 className="font-semibold text-gray-900">Plain text</h4>
            </div>
            <pre className="max-h-[680px] min-h-64 overflow-auto whitespace-pre-wrap break-words p-5 font-mono text-sm leading-6 text-gray-800">
              {plainText}
            </pre>
          </section>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-semibold text-gray-900">Exact source binding</h4>
              <StatusBadge value={approvalStatus} />
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  SHA-256 checksum
                </dt>
                <dd className="mt-1 break-all rounded bg-gray-50 p-2 font-mono text-xs text-gray-800">
                  {checksum}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Confirmation version
                </dt>
                <dd className="mt-1 break-all font-mono text-xs text-gray-800">
                  {confirmationVersion || '—'}
                </dd>
              </div>
            </dl>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h4 className="font-semibold text-gray-900">Approval history</h4>
            {approvalHistory.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">No approval record for this version.</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {approvalHistory.map((approval, index) => (
                  <li
                    key={approval.id || `${approval.createdAt || approval.created_at}-${index}`}
                    className="rounded-lg bg-gray-50 p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-gray-900">
                        {approval.actorMasked ||
                          approval.actor_masked ||
                          approval.approvedBy ||
                          'Founder'}
                      </span>
                      <StatusBadge value={approval.status || 'approved'} />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatDateTime(approval.createdAt || approval.created_at)}
                    </p>
                    {approval.evidence ? (
                      <p className="mt-2 text-xs text-gray-700">{approval.evidence}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </PageState>

      {dialog === 'approve' ? (
        <Modal
          title="Approve this exact template?"
          description={`Version ${version} with checksum ${checksum} will be recorded. A later source or partial change invalidates this approval.`}
          confirmLabel="Approve exact checksum"
          confirmDisabled={!approvalEvidenceComplete}
          busy={busy}
          onConfirm={approve}
          onClose={() => !busy && setDialog(null)}
        >
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <ExclamationTriangleIcon className="h-5 w-5 flex-none" />
            Approval does not activate a campaign. Campaign launch gates remain separate.
          </div>
          <fieldset className="mt-4">
            <legend className="text-sm font-semibold text-gray-900">
              Confirm each review surface
            </legend>
            <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
              {Object.keys(EMPTY_EVIDENCE).map((key) => (
                <label
                  key={key}
                  className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm text-gray-700 hover:bg-gray-50 ${focusRing}`}
                >
                  <input
                    type="checkbox"
                    checked={approvalEvidence[key]}
                    onChange={(event) =>
                      setApprovalEvidence((current) => ({
                        ...current,
                        [key]: event.target.checked,
                      }))
                    }
                    className="h-5 w-5 rounded border-gray-300 text-blue-600"
                  />
                  {humanize(key)}
                </label>
              ))}
            </div>
          </fieldset>
        </Modal>
      ) : null}

      {dialog === 'test' ? (
        <Modal
          title="Queue an internal test?"
          description="Only a configured internal address and the selected synthetic fixture can be used."
          confirmLabel="Queue internal test"
          confirmDisabled={!addressLooksValid}
          busy={busy}
          onConfirm={testSend}
          onClose={() => !busy && setDialog(null)}
        >
          <label className="block">
            <span className="text-sm font-semibold text-gray-800">Internal allowlist</span>
            <input
              type="email"
              autoComplete="email"
              value={selectedAddress}
              onChange={(event) => setSelectedAddress(event.target.value)}
              placeholder="founder@covenantlabs.com"
              className={`mt-2 min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm ${focusRing}`}
            />
            <span className="mt-1 block text-xs text-gray-500">
              The server accepts only one of {template.testAllowlistCount || 0} configured internal
              addresses.
            </span>
          </label>
          <p className="mt-3 text-sm text-gray-600">
            Fixture: <strong>{humanize(variant)}</strong>. This action creates a distinct audited
            test delivery.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
